import { useEffect, useMemo, useRef, useState } from "react";
import { coreGet, coreMutate } from "../../core-api/api-client";
import { nodeState, toggleNode, type PermissionNode } from "./permission-tree";
import "./roles.css";

interface Role {
  id: string;
  name: string;
  code: string;
  notificationPersona: "SALES" | "SOURCING" | null;
  version: number;
  readOnly: boolean;
  policyVersion: number;
  nodeKeys: string[];
  effectivePermissions: string[];
}

interface Directory {
  policyVersion: number;
  catalog: PermissionNode[];
  grantableNodeKeys: string[];
  canManage: boolean;
  isPlatformAdministrator: boolean;
  roles: Role[];
}

const endpoint = "/auth/admin/roles";
const automaticAuditReason = "通过角色管理页面保存";

export function RoleManagement({ onSaved }: { onSaved: () => Promise<void> }) {
  const [directory, setDirectory] = useState<Directory>();
  const [roleId, setRoleId] = useState("");
  const [name, setName] = useState("");
  const [notificationPersona, setNotificationPersona] = useState<
    "SALES" | "SOURCING" | ""
  >("");
  const [selected, setSelected] = useState(new Set<string>());
  const [search, setSearch] = useState("");
  const [hideLegacyRoles, setHideLegacyRoles] = useState(true);
  const [hideLegacyPermissions, setHideLegacyPermissions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const generation = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    void coreGet<Directory>(endpoint, controller.signal)
      .then((next) => {
        setDirectory(next);
        const firstVisibleRole = next.roles.find(
          (item) => item.policyVersion !== 0,
        );
        if (firstVisibleRole) {
          setRoleId(firstVisibleRole.id);
          setName(firstVisibleRole.name);
          setNotificationPersona(firstVisibleRole.notificationPersona ?? "");
          setSelected(new Set(firstVisibleRole.nodeKeys));
        }
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : "角色加载失败");
      });
    return () => {
      controller.abort();
      generation.current++;
    };
  }, []);

  const role = directory?.roles.find((item) => item.id === roleId);
  const visibleRoles = useMemo(() => {
    const keyword = search.trim().toLocaleLowerCase();
    return (directory?.roles ?? []).filter((item) => {
      if (hideLegacyRoles && item.policyVersion === 0) return false;
      if (!keyword) return true;
      return `${item.name} ${item.code}`.toLocaleLowerCase().includes(keyword);
    });
  }, [directory?.roles, hideLegacyRoles, search]);
  const legacyRoleCount =
    directory?.roles.filter((item) => item.policyVersion === 0).length ?? 0;
  const exceedsCeiling =
    role?.nodeKeys.some((key) => !directory?.grantableNodeKeys.includes(key)) ??
    false;
  const readOnly =
    !directory?.canManage || Boolean(role?.readOnly) || exceedsCeiling;

  const choose = (id: string) => {
    const next = directory?.roles.find((item) => item.id === id);
    setRoleId(id);
    setName(next?.name ?? "");
    setNotificationPersona(next?.notificationPersona ?? "");
    setSelected(new Set(next?.nodeKeys ?? []));
    setError("");
    setNotice("");
  };

  const save = async () => {
    if (!directory || readOnly || busy) return;
    const currentGeneration = generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await coreMutate<{ id: string; version: number }>(
        roleId ? `${endpoint}/${roleId}` : endpoint,
        roleId ? "PUT" : "POST",
        {
          name,
          notificationPersona: notificationPersona || null,
          reason: automaticAuditReason,
          policyVersion: directory.policyVersion,
          nodeKeys: [...selected],
          ...(role ? { expectedVersion: role.version } : {}),
        },
      );
      if (currentGeneration !== generation.current) return;
      const savedRole: Role = {
        id: result.id,
        version: result.version,
        name,
        notificationPersona: notificationPersona || null,
        code: role?.code ?? "CUSTOM",
        readOnly: false,
        policyVersion: directory.policyVersion,
        nodeKeys: [...selected],
        effectivePermissions: directory.catalog
          .filter((node) => node.permission && selected.has(node.key))
          .map((node) => node.permission!),
      };
      setDirectory({
        ...directory,
        roles: [
          ...directory.roles.filter((item) => item.id !== result.id),
          savedRole,
        ],
      });
      setRoleId(result.id);
      setNotice("角色已保存，权限将在后续请求中生效。");
      await onSaved();
      const next = await coreGet<Directory>(endpoint);
      if (currentGeneration !== generation.current) return;
      setDirectory(next);
    } catch (cause) {
      if (currentGeneration === generation.current)
        setError(
          cause instanceof Error ? cause.message : "保存失败，请重新加载角色",
        );
    } finally {
      if (currentGeneration === generation.current) setBusy(false);
    }
  };

  const reload = async () => {
    setBusy(true);
    setError("");
    try {
      const next = await coreGet<Directory>(endpoint);
      setDirectory(next);
      const current = next.roles.find((item) => item.id === roleId);
      setSelected(new Set(current?.nodeKeys ?? []));
      setName(current?.name ?? "");
      setNotificationPersona(current?.notificationPersona ?? "");
      setRoleId(current?.id ?? "");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "加载失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="role-management" aria-label="角色管理">
      <header className="role-header">
        <div>
          <h1>角色管理</h1>
          <p>管理当前工作空间的角色和功能权限</p>
        </div>
        <div className="role-actions">
          <button type="button" disabled={busy} onClick={() => void reload()}>
            刷新
          </button>
          <button
            className="primary"
            type="button"
            disabled={!directory?.canManage || busy}
            onClick={() => choose("")}
          >
            新建角色
          </button>
        </div>
      </header>
      {error && (
        <div role="alert" className="role-message role-error">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="role-message role-notice">
          {notice}
        </div>
      )}
      {!directory ? (
        <p className="role-loading">正在加载权限树…</p>
      ) : (
        <div className="role-management-layout">
          <aside className="role-list" aria-label="角色列表">
            <label className="role-search">
              搜索角色
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="角色名称或编码"
              />
            </label>
            <label className="role-filter">
              <input
                type="checkbox"
                checked={hideLegacyRoles}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setHideLegacyRoles(checked);
                  if (checked && role?.policyVersion === 0) {
                    const firstVisibleRole = directory.roles.find(
                      (item) => item.policyVersion !== 0,
                    );
                    choose(firstVisibleRole?.id ?? "");
                  }
                }}
              />
              隐藏老系统角色
              {legacyRoleCount > 0 && <span>{legacyRoleCount}</span>}
            </label>
            <p className="role-muted">{visibleRoles.length} 个角色</p>
            <div className="role-items">
              {visibleRoles.map((item) => (
                <button
                  className="role-item"
                  type="button"
                  key={item.id}
                  aria-pressed={roleId === item.id}
                  disabled={busy}
                  onClick={() => choose(item.id)}
                >
                  <strong>{item.name}</strong>
                  <span>
                    {item.policyVersion === 0
                      ? "老系统角色"
                      : item.readOnly
                        ? "预置角色 · 只读"
                        : "自定义角色"}
                  </span>
                </button>
              ))}
              {!visibleRoles.length && (
                <p className="role-empty">
                  {search ? "没有匹配的角色" : "当前没有可显示的角色"}
                </p>
              )}
            </div>
          </aside>
          <main className="role-detail">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <div className="role-detail-title">
                <div>
                  <h2>{role ? role.name : "新建角色"}</h2>
                  <p>
                    {role
                      ? "配置角色名称和可使用的功能权限。"
                      : "填写角色名称并选择该角色可使用的功能。"}
                  </p>
                </div>
                {role && (
                  <button
                    type="button"
                    disabled={!directory.canManage || exceedsCeiling || busy}
                    onClick={() => {
                      setRoleId("");
                      setName(`${name} 副本`);
                      setNotificationPersona("");
                      setNotice("");
                    }}
                  >
                    复制为新角色
                  </button>
                )}
              </div>
              <label className="role-name">
                角色名称
                <input
                  value={name}
                  maxLength={160}
                  required
                  disabled={readOnly || busy}
                  placeholder="请输入角色名称"
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label className="role-name">
                询单邮件职责
                <select
                  value={notificationPersona}
                  disabled={readOnly || busy}
                  onChange={(event) =>
                    setNotificationPersona(
                      event.target.value as "SALES" | "SOURCING" | "",
                    )
                  }
                >
                  <option value="">不参与询单邮件分发</option>
                  <option value="SALES">
                    销售：接收原始询单并抄送查货通知
                  </option>
                  <option value="SOURCING">
                    查货：接收原始询单密送及查货通知
                  </option>
                </select>
                <span className="role-muted">
                  每个工作空间只能有一个有效角色绑定销售职责、一个绑定查货职责。
                </span>
              </label>
              {role?.readOnly && (
                <p className="role-guidance">
                  预置及平台角色受保护，可复制当前权限后创建新角色。
                </p>
              )}
              {exceedsCeiling && (
                <p className="role-guidance">
                  此角色包含超出你授权范围的权限，仅可查看。
                </p>
              )}
              {role?.policyVersion === 0 && (
                <p className="role-guidance">
                  这是老系统角色，当前显示的是兼容映射，不能直接修改。
                </p>
              )}
              <section
                className="role-permissions"
                aria-labelledby="role-permissions-title"
              >
                <div className="role-permissions-header">
                  <div>
                    <h3 id="role-permissions-title">功能权限</h3>
                    <p>勾选父级后，可以继续配置其下的页面和操作权限。</p>
                  </div>
                  <label className="role-filter">
                    <input
                      type="checkbox"
                      checked={hideLegacyPermissions}
                      onChange={(event) =>
                        setHideLegacyPermissions(event.target.checked)
                      }
                    />
                    隐藏现有功能权限（兼容期）
                  </label>
                </div>
                <fieldset disabled={readOnly || busy}>
                  <legend className="sr-only">功能权限</legend>
                  <PermissionBranch
                    directory={directory}
                    selected={selected}
                    hideLegacyPermissions={hideLegacyPermissions}
                    onToggle={(key, checked) =>
                      setSelected((previous) =>
                        toggleNode(
                          directory.catalog,
                          previous,
                          key,
                          checked,
                          new Set(directory.grantableNodeKeys),
                        ),
                      )
                    }
                  />
                </fieldset>
              </section>
              <div className="role-form-actions">
                <button
                  className="primary"
                  type="submit"
                  disabled={readOnly || busy || !name.trim()}
                >
                  {busy ? "保存中…" : "保存角色"}
                </button>
              </div>
            </form>
          </main>
        </div>
      )}
    </section>
  );
}

function PermissionBranch({
  directory,
  selected,
  onToggle,
  hideLegacyPermissions,
  parentKey,
}: {
  directory: Directory;
  selected: Set<string>;
  onToggle: (key: string, checked: boolean) => void;
  hideLegacyPermissions: boolean;
  parentKey?: string;
}) {
  const nodes = directory.catalog.filter(
    (node) =>
      node.parentKey === parentKey &&
      !(hideLegacyPermissions && node.key === "legacy"),
  );
  return (
    <ul>
      {nodes.map((node) => (
        <li key={node.key}>
          <NodeCheckbox
            node={node}
            state={nodeState(directory.catalog, selected, node.key)}
            disabled={
              !directory.grantableNodeKeys.includes(node.key) ||
              Boolean(node.parentKey && !selected.has(node.parentKey))
            }
            onToggle={onToggle}
          />
          {directory.catalog.some((child) => child.parentKey === node.key) && (
            <PermissionBranch
              directory={directory}
              selected={selected}
              onToggle={onToggle}
              hideLegacyPermissions={hideLegacyPermissions}
              parentKey={node.key}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

function NodeCheckbox({
  node,
  state,
  disabled,
  onToggle,
}: {
  node: PermissionNode;
  state: { checked: boolean; mixed: boolean };
  disabled: boolean;
  onToggle: (key: string, checked: boolean) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (input.current) input.current.indeterminate = state.mixed;
  }, [state.mixed]);
  return (
    <label className="permission-node">
      <input
        ref={input}
        type="checkbox"
        checked={state.checked}
        aria-checked={state.mixed ? "mixed" : state.checked}
        disabled={disabled}
        onChange={(event) => onToggle(node.key, event.target.checked)}
      />
      <span>
        {node.label}
        {node.permission && <small>{node.permission}</small>}
      </span>
    </label>
  );
}
